import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { deleteCategory } from "../../redux/actions/categoryActions";
import Loader from "../../components/Loader";
import DefaultLayout from "../../layouts/DefaultLayout";
import Alert from "../../components/Alert";
import { baseURLFile } from "../../constants";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  deleteBanner,
  getListBanners,
} from "../../redux/actions/bannerActions";
import Paginate from "../../components/Paginate";

function BannerScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const page = searchParams.get("page") || "1";

  const [eventType, setEventType] = useState("");
  const [bannerId, setBannerId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loadEvent, setLoadEvent] = useState(false);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const listBanners = useSelector((state) => state.getListBanners);
  const { banners, loadingListBanners, errorListBanners, pages } = listBanners;

  const bannerDelete = useSelector((state) => state.deleteBanner);
  const { successBannerDelete, loadingBannerDelete, errorBannerDelete } =
    bannerDelete;

  const redirect = "/";

  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    } else {
      dispatch(getListBanners(page));
    }
  }, [navigate, userInfo, dispatch, page]);

  useEffect(() => {
    if (successBannerDelete) {
      dispatch(getListBanners(1));
    }
  }, [successBannerDelete]);

  return (
    <DefaultLayout>
      <div>
        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a href="/dashboard" className="inline-flex items-center text-sm font-medium text-primary hover:text-secondary transition-colors duration-200">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
                Dashboard
              </a>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-secondary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-secondary md:ml-2">Banners</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header with Banner Image */}
        <div className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg" style={{backgroundImage: "url('/assets/background-auth-screen.png')", backgroundSize: "cover", backgroundPosition: "center"}}>
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12 flex flex-col md:flex-row justify-between items-center">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Banner Management</h1>
              <p className="text-white/80 max-w-xl">Create and manage promotional banners for your restaurant application. Attract customers with eye-catching visuals.</p>
            </div>
            <a
              href="/banners/new-banner"
              className="mt-4 md:mt-0 flex items-center px-5 py-3 bg-white text-primary font-medium rounded-lg shadow-md hover:bg-gray-50 transition-all duration-200"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-5 h-5 mr-2"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Add New Banner
            </a>
          </div>
        </div>
        {/*  */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-primary/10">
            <h2 className="font-semibold text-primary">All Banners</h2>
            <p className="text-sm text-secondary mt-1">Manage your promotional banners</p>
          </div>

          <div className="p-6">
            {loadingListBanners ? (
              <Loader />
            ) : errorListBanners ? (
              <Alert type={"error"} message={errorListBanners} />
            ) : banners?.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-primary">No banners</h3>
                <p className="mt-1 text-sm text-secondary">Get started by creating a new banner.</p>
                <div className="mt-6">
                  <a href="/banners/new-banner" className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    New Banner
                  </a>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {banners?.map((item, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden border border-primary/10 transition-all duration-200 hover:shadow-md group">
                    <div className="relative aspect-video overflow-hidden bg-secondary/5">
                      <img
                        src={baseURLFile + item.banner_image}
                        alt={item.banner_title || "Banner image"}
                        className="w-full h-full object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute top-2 right-2">
                        <button
                          onClick={() => {
                            setEventType("delete");
                            setBannerId(item.id);
                            setIsOpen(true);
                          }}
                          className="p-2 bg-white/90 rounded-full text-red-500 hover:bg-red-500 hover:text-white transition-colors duration-200 shadow-md"
                          title="Delete banner"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-medium text-primary mb-1 truncate" title={item.banner_title || "Untitled Banner"}>{item.banner_title || "Untitled Banner"}</h3>
                          <p className="text-sm text-secondary">ID: #{item.id}</p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {item.banner_type || "Default"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {pages > 1 && (
              <div className="mt-8 border-t border-primary/10 pt-6">
                <Paginate
                  route={"/banners?"}
                  search={""}
                  page={page}
                  pages={pages}
                />
              </div>
            )}
          </div>
        </div>
        <ConfirmationModal
          isOpen={isOpen}
          message={
            eventType === "delete"
              ? "Are you sure you want to delete this Banner?"
              : "Are you sure ?"
          }
          onConfirm={async () => {
            if (eventType === "cancel") {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setBannerId("");
            } else if (eventType === "delete" && bannerId !== "") {
              setLoadEvent(true);
              dispatch(deleteBanner(bannerId));
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
            } else {
              setIsOpen(false);
              setEventType("");
              setLoadEvent(false);
              setBannerId("");
            }
          }}
          onCancel={() => {
            setIsOpen(false);
            setEventType("");
            setLoadEvent(false);
            setBannerId("");
          }}
          loadEvent={loadEvent}
        />
      </div>
    </DefaultLayout>
  );
}

export default BannerScreen;
